import { Button, message } from "antd";
import PropTypes from "prop-types";
import { useCallback, useEffect, useRef, useState } from "react";

const DEFAULT_JOIN_TEXT = "Join The Movement";
let modalPreloadPromise = null;

const loadJoinModal = async () => {
    if (!modalPreloadPromise) {
        modalPreloadPromise = import("./JoinTheMomentModalV2");
    }
    return modalPreloadPromise;
};

export default function LazyJoinTheMomentModal({
    isAlreadyJoined = false,
    joinBtnProps = {},
    openSignal = 0,
    hideTrigger = false,
    ...modalProps
}) {
    const [Modal, setModal] = useState(null);
    const [isLoading, setIsLoading] = useState(false);
    const [internalOpenSignal, setInternalOpenSignal] = useState(0);
    const lastExternalSignalRef = useRef(openSignal);

    // Preload once globally during idle time so table rows don't each trigger work.
    useEffect(() => {
        if (modalPreloadPromise) return;
        const preload = () => { loadJoinModal(); };
        if (typeof requestIdleCallback !== "undefined") {
            const id = requestIdleCallback(preload, { timeout: 5000 });
            return () => cancelIdleCallback(id);
        }
        const id = setTimeout(preload, 1000);
        return () => clearTimeout(id);
    }, []);

    const handleOpen = useCallback(async () => {
        if (isAlreadyJoined || joinBtnProps?.disabled) {
            message.info("Already Joined");
            return;
        }
        if (Modal) {
            setInternalOpenSignal((s) => s + 1);
            return;
        }
        if (isLoading) return;
        setIsLoading(true);
        try {
            const mod = await loadJoinModal();
            setModal(() => mod.default);
            setInternalOpenSignal((s) => s + 1);
        } finally {
            setIsLoading(false);
        }
    }, [Modal, isAlreadyJoined, isLoading, joinBtnProps?.disabled]);

    useEffect(() => {
        if (!openSignal) return;
        if (openSignal === lastExternalSignalRef.current) return;
        lastExternalSignalRef.current = openSignal;
        handleOpen();
    }, [openSignal, handleOpen]);

    const hiddenBtnProps = {
        ...joinBtnProps,
        children: joinBtnProps?.children ?? DEFAULT_JOIN_TEXT,
        style: { ...joinBtnProps?.style, display: "none" },
    };

    return (
        <>
            {!hideTrigger && (
                <Button
                    {...joinBtnProps}
                    disabled={Boolean(joinBtnProps?.disabled || isAlreadyJoined)}
                    loading={Boolean(joinBtnProps?.loading || isLoading)}
                    onClick={handleOpen}
                >
                    {joinBtnProps?.children ?? DEFAULT_JOIN_TEXT}
                </Button>
            )}
            {Modal && (
                <Modal
                    {...modalProps}
                    isAlreadyJoined={isAlreadyJoined}
                    joinBtnProps={hiddenBtnProps}
                    openSignal={internalOpenSignal}
                />
            )}
        </>
    );
}

LazyJoinTheMomentModal.propTypes = {
    isAlreadyJoined: PropTypes.bool,
    joinBtnProps: PropTypes.object,
    openSignal: PropTypes.number,
    hideTrigger: PropTypes.bool,
};
